/**
 * Calder & Wynn: the fictional professional services firm the landing
 * page's stories are set in (fixture-clients.ts, fixture-decisions.ts). One
 * workspace, so every story shows the same firm, people, channels and
 * connected sources. Every name is invented for the site (the firm, its
 * people, its clients and suppliers); none is a customer, prospect or demo
 * tenant, and any address uses an example domain.
 *
 * Dates sit in the week of Thursday 24 September 2026, a week before
 * month-end on Wednesday the 30th; each story's question was asked at 09:11.
 */

import type { ProvenanceActor } from "@product/components/app/runs/provenance-avatar-stack";
import type { ComposerSource } from "@product/components/app/sessions/composer-sources-view";
import type { WorkspaceChannel, WorkspaceField, WorkspaceSession } from "@product/lib/mlx-workspace-client";

import type { ShotTurnTimes, ShotWorkspace } from "./workspace";

const DAY = 86_400_000;
const MINUTE = 60_000;

/** Thursday 24 September 2026, 09:12 London time. */
export const CALDER_NOW = Date.UTC(2026, 8, 24, 8, 12);

export const CALDER_USER: ProvenanceActor = { label: "Maya Hart", imageUrl: null, initials: "MH" };

export function calderSession(id: string, title: string, status: string, ageMinutes: number): WorkspaceSession {
  return {
    id,
    title,
    status,
    createdAt: CALDER_NOW - ageMinutes * MINUTE,
    updatedAt: CALDER_NOW - ageMinutes * MINUTE,
    visibility: "private",
  };
}

function channel(id: string, name: string, ageDays: number): WorkspaceChannel {
  return { id, name, status: "active", createdAt: CALDER_NOW - ageDays * DAY, updatedAt: CALDER_NOW - ageDays * DAY };
}

export const CALDER_CHANNELS = {
  accounts: channel("ch_accounts", "Accounts", 0),
  operations: channel("ch_operations", "Operations", 0),
  finance: channel("ch_finance", "Finance", 1),
  delivery: channel("ch_delivery", "Delivery", 1),
  sales: channel("ch_sales", "Sales", 2),
  people: channel("ch_people", "People", 9),
} as const;

/**
 * The firm's connected sources as the composer's sources row lists them
 * (ComposerSourcesView), with the icon the app's registry gives each.
 */
const COMPOSER_SOURCES: readonly ComposerSource[] = [
  { id: "hubspot", label: "HubSpot", pluginSlugs: ["hubspot"], iconUrl: "/concepts/product-shot/icons/sources/hubspot.svg" },
  { id: "xero", label: "Xero", pluginSlugs: ["xero"], iconUrl: "/concepts/product-shot/icons/sources/xero.svg" },
  {
    id: "microsoft-outlook",
    label: "Microsoft Outlook Mail",
    pluginSlugs: ["microsoft-outlook"],
    iconUrl: "/concepts/product-shot/icons/sources/outlook-mail.svg",
  },
  {
    id: "google-drive",
    label: "Google Drive",
    pluginSlugs: ["google-drive"],
    iconUrl: "/concepts/product-shot/icons/sources/google-drive.png",
  },
];

/** The rest of the firm's recent sessions, newest first; one is still running. */
const OTHER_SESSIONS: WorkspaceSession[] = [
  calderSession("ses_sept_invoices", "Reconcile September invoices", "running", 7),
  calderSession("ses_q4_pipeline", "Q4 pipeline by owner", "completed", 45),
  calderSession("ses_october_renewals", "Renewals due in October", "completed", 110),
  calderSession("ses_utilisation", "Team utilisation, September", "completed", 1 * 24 * 60),
  calderSession("ses_board_pack", "Board pack figures", "completed", 2 * 24 * 60),
  calderSession("ses_supplier_contracts", "Supplier contracts up for renewal", "completed", 3 * 24 * 60),
  calderSession("ses_kestrel_handover", "Kestrel onboarding handover", "completed", 6 * 24 * 60),
  calderSession("ses_hiring_plan", "Hiring plan for Q1", "completed", 8 * 24 * 60),
];

/**
 * The workspace around one story's session: it heads the rail (asked a
 * minute ago), before the firm's other sessions.
 */
export function calderWorkspace(open: { sessionId: string; sessionTitle: string }): ShotWorkspace {
  return {
    organisationName: "Calder & Wynn",
    user: CALDER_USER,
    ownerId: "usr_maya",
    channels: Object.values(CALDER_CHANNELS),
    sessions: [calderSession(open.sessionId, open.sessionTitle, "completed", 1), ...OTHER_SESSIONS],
    pinnedSessions: [calderSession("ses_month_end_cash", "Month-end cash position", "completed", 12 * 24 * 60)],
    unreadSessionIds: ["ses_october_renewals"],
    activeSessionId: open.sessionId,
    sessionTitle: open.sessionTitle,
    composerSources: COMPOSER_SOURCES,
  };
}

/** The rail while the open session's run works. */
export function sessionsRunning(workspace: ShotWorkspace): WorkspaceSession[] {
  return workspace.sessions.map((entry) =>
    entry.id === workspace.activeSessionId ? { ...entry, status: "running" } : entry,
  );
}

/** A question asked at 09:11, as the transcript prints it in the browser
 *  (formatDateTime's en-GB rendering, as Ostrey's; formatElapsedAgo). */
export const CALDER_TURN_TIMES: ShotTurnTimes = {
  askedAt: CALDER_NOW - MINUTE,
  dateTitle: "24 Sept 2026, 09:11",
  timeLabel: "1 min ago",
};

/**
 * A Data Set field a workflow keeps current from the connected systems, so
 * the record sheet prints its value rather than an editor.
 */
export function workflowField(
  key: string,
  label: string,
  type: WorkspaceField["type"],
  config: NonNullable<WorkspaceField["config"]> = {},
): WorkspaceField {
  return { id: `fld_${key}`, key, label, type, required: false, status: "active", config: { valueOwner: "workflow", ...config } };
}

/** Where the product links a record: its channel, with the record open. */
export function calderRecordHref(channelId: string, recordId: string): string {
  return `/workspace/channels/${channelId}?record=${recordId}`;
}

/** Whole days from one date to another, both yyyy-mm-dd. */
export function daysBetween(from: string, to: string): number {
  return Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / DAY);
}
